import React, { Component } from 'react'
import PropTypes from 'prop-types'
import { observer, inject } from 'mobx-react'

import inlineStyles from './inlineStyles'
import IfIE from '@client/components/IfIE'
import Column from '@client/components/Column/index'
import { BLOCK_TYPES } from '@client/components/Block/constants'
import {
  BlockModel,
  NULL as BlockModelNULL,
} from '@client/components/Block/model'
import EditorModel, {
  NULL as EditorModelNULL,
} from '@client/components/Editor/model'
import {
  ITEM_TYPE_CONFIRMATION_MAIL,
  ITEM_TYPE_FORM,
  ITEM_TYPE_MAIL,
  ITEM_TYPE_RESPONDER,
  BUTTON_BORDER_STYLES,
} from '@client/components/Editor/settings'
import {
  SECTION_VERTICAL_ALIGN_EMAIL_MAP,
  SECTION_TYPES
} from '@client/components/Section/constants'

const commentImageStart = (url, color, width = 642) => {
  return (
    `<v:rect xmlns:v="urn:schemas-microsoft-com:vml" fill="true" stroke="false" style="width:${width}px;">` +
    `<v:fill type="tile" src="${url || ''}" color="${color || ''}" />` +
    '<v:textbox style="mso-fit-shape-to-text:true" inset="0,0,0,0">'
  )
}

const commentColumnStart = ({ color, model, kind, index }) => {
  const columns = model.columns.length
  let width = 642
  let valign = 'top'
  if (columns === 2) {
    if ((kind === ITEM_TYPE_MAIL ||
      kind === ITEM_TYPE_CONFIRMATION_MAIL ||
      kind === ITEM_TYPE_RESPONDER) &&
      model.sectionType == SECTION_TYPES.TWOCOL
    ) {
      valign = SECTION_VERTICAL_ALIGN_EMAIL_MAP[model.settings.values.alignItems] || 'top'

      const columnStyles = model.columnStyles
      const initialColumnMaxWidth = parseFloat(`${columnStyles.maxWidth || 0}`.replace('px', '')) || 0
      const initialTotalMaxWidth = initialColumnMaxWidth * 2

      if (index === 0) {
        width = (initialTotalMaxWidth *
          (model.settings.values.firstColumnWidthPersent || 50)) /
        100
      } else {
        width = (initialTotalMaxWidth *
          (100 - (model.settings.values.firstColumnWidthPersent || 50))) /
        100
      }
    } else {
      width = 321
    }
  } else if (columns === 3) {
    width = 214
  }
  return color
    ? `<td bgcolor="${color}" valign="${valign}" width="${width}">`
    : `<td valign="${valign}" width="${width}">`
}

const acceptTypes = Object.values(BLOCK_TYPES).filter(
  (item) => item !== BLOCK_TYPES.NULL && item !== BLOCK_TYPES.SECTION
)

const classNameByColumnCount = ['', 'one-column', 'two-column', 'three-column']

@inject('editor', 'serverRender')
@observer
class TableInner extends Component {
  static propTypes = {
    serverRender: PropTypes.bool,
    model: PropTypes.instanceOf(BlockModel),
    editor: PropTypes.instanceOf(EditorModel),
  }

  static defaultProps = {
    serverRender: false,
    model: BlockModelNULL,
    editor: EditorModelNULL,
  }

  render() {
    const { model, editor, serverRender } = this.props
    const settings = model.settings.values

    const preparedAcceptTypes = (
      model.sectionType === SECTION_TYPES.PRODUCTSTHREECOL ||
      model.sectionType === SECTION_TYPES.PRODUCTSTWOCOL
    )
      ? [BLOCK_TYPES.PRODUCT] 
      : acceptTypes

    let tableStyles = {
      ...inlineStyles.innerTable,
      ...model.getInnerTableStyles(editor.settings),
    }

    let tableCellStyles = { padding: '0' }

    if (model.columns.length > 1) {
      tableCellStyles.textAlign = 'center'

      if (serverRender) {
        tableCellStyles.fontSize = '0'
      }
    }

    const backgroundColor =
      model.settings.values.backgroundColor ||
      editor.settings.values.contentColor
    const backgroundImage =
      model.isContentWidth && settings.hasBackgroundImage
        ? settings.backgroundImageUrl || ''
        : ''

    if (backgroundImage) {
      tableCellStyles.backgroundPosition = 'center'
      tableCellStyles.backgroundSize = 'cover'
    }

    if (editor.kind === ITEM_TYPE_FORM) {
      tableCellStyles.display = 'flex'
    }

    if (
      (editor.kind === ITEM_TYPE_MAIL ||
      editor.kind === ITEM_TYPE_CONFIRMATION_MAIL ||
      editor.kind === ITEM_TYPE_RESPONDER) &&
      model.sectionType == SECTION_TYPES.TWOCOL
    ) {
      tableCellStyles.verticalAlign = SECTION_VERTICAL_ALIGN_EMAIL_MAP[model.settings.values.alignItems] || 'top'
    }

    if (
      model.settings.values.borderStyle &&
      model.settings.values.borderStyle !== BUTTON_BORDER_STYLES[0].value &&
      model.isContentWidth
    ) {
      tableStyles = { ...tableStyles, ...model.borderStyles, ...model.borderRadiusStyles }
    }

    return (
      <table
        className={serverRender ? '' : 'section-inner'}
        role="presentation"
        cellSpacing="0"
        cellPadding="0"
        style={tableStyles}
        border="0"
        align="center"
        ref={(el) => (this.dom = el)}
      >
        <tbody>
          <tr>
            <td
              className={`${classNameByColumnCount[model.columns.length]} ${
                model.hasGutter ? 'with-gutter' : ''
              }`}
              valign={SECTION_VERTICAL_ALIGN_EMAIL_MAP[model.settings.values.alignItems] || 'top'}
              style={tableCellStyles}
              align="center"
              background={backgroundImage}
              bgcolor={model.isContentWidth ? backgroundColor : ''}
            >
              <If condition={model.isContentWidth && backgroundImage}>
                <IfIE
                  browsers="(gte mso 9)|(IE)"
                  comment={commentImageStart(
                    backgroundImage,
                    model.isContentWidth ? backgroundColor : ''
                  )}
                />
              </If>

              <If condition={model.columns.length > 1}>
                <IfIE
                  browsers="(gte mso 9)|(IE)"
                  comment={
                    '<table role="presentation" cellspacing="0" cellpadding="0" border="0" width="100%"><tr>'
                  }
                />
              </If>

              <For each="column" index="index" of={model.columns}>
                <If condition={index > 0 && model.hasGutter}>
                  <div
                    className="gutter"
                    style={{
                      display: 'inline-block',
                      height: '20px',
                      width: '20px',
                    }}
                    dangerouslySetInnerHTML={{
                      __html: '&nbsp;',
                    }}
                  />
                  <IfIE
                    browsers="(gte mso 9)|(IE)"
                    comment={'<td width="20" valign="top">&nbsp;</td>'}
                  />
                </If>

                <If condition={model.columns.length > 1}>
                  <IfIE
                    browsers="(gte mso 9)|(IE)"
                    comment={commentColumnStart({
                      color: model.isBoxed ? backgroundColor : '',
                      model,
                      index,
                      kind: editor.kind,
                    })}
                  />
                </If>

                <Column
                  column={column}
                  key={column.id}
                  acceptTypes={preparedAcceptTypes}
                  model={model}
                  index={index}
                />

                <If condition={model.columns.length > 1}>
                  <IfIE browsers="(gte mso 9)|(IE)" comment={'</td>'} />
                </If>
              </For>

              <If condition={model.columns.length > 1}>
                <IfIE browsers="(gte mso 9)|(IE)" comment={'</tr></table>'} />
              </If>

              <If condition={model.isContentWidth && backgroundImage}>
                <IfIE
                  browsers="(gte mso 9)|(IE)"
                  comment={'</v:textbox></v:rect>'}
                />
              </If>
            </td>
          </tr>
        </tbody>
      </table>
    )
  }
}

export default TableInner
